import Image from "next/image"
import Link from "next/link"
import {
  MDXRemote,
  type MDXRemoteProps,
  type MDXRemoteSerializeResult,
} from "next-mdx-remote"
import type React from "react"
import ApplicationCTA from "@/components/careers/ApplicationCTA"
import NicerBlockquote from "@/components/common/NicerBlockquote"
import type { SharedContent } from "@/utils/careers-mdx"

// Import the BlockquoteProps interface directly
interface BlockquoteProps {
  content: React.ReactNode
  author: string
  authorTitle: string
  authorImage: string
}

interface ApplicationCTAProps {
  title?: string
  email?: string
  role?: string
}

type MDXComponents = NonNullable<MDXRemoteProps["components"]>

// Create base components with shared styling
export const baseComponents = {
  // HTML overrides with Tailwind styling
  p: ({ children }: { children: React.ReactNode }) => (
    <p className="text-fg-secondary leading-relaxed my-2 first:mt-0 text-pretty">
      {children}
    </p>
  ),
  h1: ({ children }: { children: React.ReactNode }) => (
    <h1 className="font-medium mt-10 mb-2 first:mt-0 text-3xl tracking-tight">
      {children}
    </h1>
  ),
  h2: ({ children }: { children: React.ReactNode }) => (
    <h2 className="font-medium mt-10 mb-2 first:mt-0 text-xl tracking-tight">
      {children}
    </h2>
  ),
  h3: ({ children }: { children: React.ReactNode }) => (
    <h3 className="font-medium mt-10 mb-2 first:mt-0 text-lg tracking-tight">
      {children}
    </h3>
  ),
  h4: ({ children }: { children: React.ReactNode }) => (
    <h4 className="font-medium mt-10 mb-2 first:mt-0 text-base tracking-tight">
      {children}
    </h4>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-medium text-fg-default">{children}</strong>
  ),
  blockquote: ({ children }: { children: React.ReactNode }) => (
    <blockquote className="py-12 my-5 text-sm leading-relaxed">
      {children}
    </blockquote>
  ),
  ul: ({ children }: { children: React.ReactNode }) => (
    <ul className="list-disc pl-6 my-2">{children}</ul>
  ),
  ol: ({ children }: { children: React.ReactNode }) => (
    <ol className="list-decimal ml-6 mb-4">{children}</ol>
  ),
  li: ({ children }: { children: React.ReactNode }) => (
    <li className="text-fg-secondary leading-relaxed mt-2">{children}</li>
  ),
  a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
    <Link
      href={href || ""}
      target="_blank"
      rel="noopener noreferrer"
      className="text-fg-emerald hover:underline"
    >
      {children}
    </Link>
  ),
  img: ({ src, alt, ...props }: { src: string; alt?: string }) => (
    <div className="my-4">
      <Image
        src={src}
        width={800}
        height={450}
        alt={alt || "Image"}
        className="rounded-lg"
        {...props}
      />
    </div>
  ),

  // Custom components with proper typing
  NicerBlockquote: (props: BlockquoteProps) => <NicerBlockquote {...props} />,
  ApplicationCTA: (props: ApplicationCTAProps) => <ApplicationCTA {...props} />,
}

// Create a helper component for shared content
const SharedMDXRenderer = ({
  source,
}: {
  source: MDXRemoteSerializeResult
}) => {
  return <MDXRemote {...source} components={baseComponents} />
}

// Create a function that returns components with shared content injected
export const getComponents = (sharedContent?: SharedContent): MDXComponents => {
  if (!sharedContent) {
    return baseComponents
  }

  return {
    ...baseComponents,
    CompanyIntro: () => (
      <SharedMDXRenderer source={sharedContent.companyIntro} />
    ),
    Architecture: () => (
      <SharedMDXRenderer source={sharedContent.architecture} />
    ),
    TechStack: () => <SharedMDXRenderer source={sharedContent.techStack} />,
    Benefits: () => <SharedMDXRenderer source={sharedContent.benefits} />,
  }
}

// Create a mapping of component names to actual React components
const components: MDXComponents = baseComponents

export default components
